// Copyright 2021 Linka Cloud  All rights reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//   http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { Box, Paper, useTheme } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import Head from 'next/head';
import React, { PropsWithChildren, ReactNode } from 'react';
import { theme } from '../theme/theme';

const center = {
  display: 'flex',
  alignItem: 'center',
  justifyContent: 'center',
};

export const Layout = ({
  children,
  top = null,
  bottom = null,
}: PropsWithChildren<any> & {
  top?: ReactNode;
  bottom?: ReactNode;
}) => {
  const theme = useTheme();
  return (
    <Box
      component="main"
      sx={{
        ...center,
        width: '100%',
        minHeight: '100%',
      }}
    >
      <Head>
        <meta
          key="theme-color-light"
          name="theme-color"
          media="(prefers-color-scheme: light)"
          content={theme.colorSchemes.light?.palette.background.default ?? '#ffffff'}
        />
        <meta
          key="theme-color-dark"
          name="theme-color"
          media="(prefers-color-scheme: dark)"
          content={theme.colorSchemes.dark?.palette.background.default ?? '#000000'}
        />
      </Head>
      <Box
        sx={{
          ...center,
          flexDirection: 'column',
          margin: { xs: '0', sm: '0 10.3rem' },
          width: { xs: '100%', sm: 'auto' },
        }}
      >
        {top}
        <Paper
          sx={{
            width: { xs: undefined, sm: '30rem' },
            padding: { xs: 2, sm: 4 },
            backgroundColor: `rgba(${theme.vars.palette.background.paperChannel} / 0.4)`,
          }}
        >
          {children}
        </Paper>
        {bottom}
      </Box>
    </Box>
  );
};

export const AppLayout = (
  props: PropsWithChildren<any> & {
    top?: ReactNode;
    bottom?: ReactNode;
  }
) => (
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <Layout {...props} />
  </ThemeProvider>
);
